/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// =============================================================================
// Complementary Products — ComplementaryProductsNav custom element
// Powers the prev/next carousel in the horizontal product card block.
// Runs from both product-content.liquid and main-product.liquid placements.
// =============================================================================

if (!customElements.get('complementary-products-nav')) {
  class ComplementaryProductsNav extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('.complementary-products-track');
      this.items = Array.from(this.querySelectorAll('.complementary-products-track__item'));
      this.prevButton = this.querySelector('.prev-next-button--prev');
      this.nextButton = this.querySelector('.prev-next-button--next');

      if (!this.scroller || !this.prevButton || !this.nextButton) {
        return;
      }

      this._syncButtons = this._syncButtons.bind(this);
      this._handlePrevClick = this._handlePrevClick.bind(this);
      this._handleNextClick = this._handleNextClick.bind(this);

      this.scroller.addEventListener('scroll', this._syncButtons, { passive: true });
      this.prevButton.addEventListener('click', this._handlePrevClick);
      this.nextButton.addEventListener('click', this._handleNextClick);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this._syncButtons);
        this.resizeObserver.observe(this.scroller);
      }

      this._syncButtons();
    }

    disconnectedCallback() {
      this.scroller?.removeEventListener('scroll', this._syncButtons);
      this.prevButton?.removeEventListener('click', this._handlePrevClick);
      this.nextButton?.removeEventListener('click', this._handleNextClick);
      this.resizeObserver?.disconnect();
    }

    _handlePrevClick(event) {
      event.preventDefault();
      this._scrollToIndex(this._getActiveIndex() - 1);
    }

    _handleNextClick(event) {
      event.preventDefault();
      this._scrollToIndex(this._getActiveIndex() + 1);
    }

    _scrollToIndex(index) {
      if (!this.items.length) {
        return;
      }

      const targetIndex = Math.max(0, Math.min(this.items.length - 1, index));
      this.items[targetIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    _getActiveIndex() {
      if (!this.items.length) {
        return 0;
      }

      const scrollerRect = this.scroller.getBoundingClientRect();
      const isRtl = getComputedStyle(this.scroller).direction === 'rtl';

      return this.items.reduce((closestIndex, item, index) => {
        const itemRect = item.getBoundingClientRect();
        const itemEdge = isRtl ? itemRect.right : itemRect.left;
        const scrollerEdge = isRtl ? scrollerRect.right : scrollerRect.left;
        const itemDistance = Math.abs(itemEdge - scrollerEdge);
        const closestRect = this.items[closestIndex].getBoundingClientRect();
        const closestEdge = isRtl ? closestRect.right : closestRect.left;
        const closestDistance = Math.abs(closestEdge - scrollerEdge);

        return itemDistance < closestDistance ? index : closestIndex;
      }, 0);
    }

    _syncButtons() {
      const activeIndex = this._getActiveIndex();
      const hasOverflow = this.scroller.scrollWidth - this.scroller.clientWidth > 1;

      this.prevButton.toggleAttribute('disabled', !hasOverflow || activeIndex <= 0);
      this.nextButton.toggleAttribute('disabled', !hasOverflow || activeIndex >= this.items.length - 1);
    }
  }

  customElements.define('complementary-products-nav', ComplementaryProductsNav);
}

if (!window.__compQuickBuyCloseFix) {
  window.__compQuickBuyCloseFix = true;

  document.addEventListener('click', (event) => {
    const closeTrigger = event.target.closest(
      '.popover--quick-buy [data-action="close"], .popover--quick-buy .popover__overlay, .drawer--quick-buy [data-action="close"], .drawer--quick-buy .drawer__overlay'
    );

    if (!closeTrigger) {
      return;
    }

    const openableElement = closeTrigger.closest('quick-buy-popover, quick-buy-drawer');

    if (!openableElement || !openableElement.id.startsWith('horizontal-product-')) {
      return;
    }

    openableElement.open = false;

    document.querySelectorAll(`[aria-controls="${openableElement.id}"]`).forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  });
}

if (!window.__stickyAtcVariantRedirect) {
  window.__stickyAtcVariantRedirect = true;

  document.addEventListener('click', (event) => {
    const stickyAtcButton = event.target.closest('.product-sticky-form [data-product-add-to-cart-button]');

    if (!stickyAtcButton || !window.matchMedia(window.themeVariables.breakpoints.pocket).matches) {
      return;
    }

    const stickyForm = stickyAtcButton.closest('product-sticky-form');
    const formId = stickyForm?.getAttribute('form-id');

    if (!formId) {
      return;
    }

    const mainVariantPicker = document.querySelector(`variant-picker[form-id="${formId}"]`);

    if (!mainVariantPicker) {
      return;
    }

    event.preventDefault();

    const scrollTop = window.scrollY + mainVariantPicker.getBoundingClientRect().top - getStickyHeaderOffset() - 24;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: Math.max(0, scrollTop),
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  });
}


/* Vertical Video Carousel section - start */
// js/custom-element/section/vertical-video-carousel/vertical-video-carousel.js
var VerticalVideoCarousel = class extends CustomHTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll(".vertical-video-carousel__item"));
    this.scroller = this.querySelector(".vertical-video-carousel__scroller");
    this.progressBar = this.querySelector(".vertical-video-carousel__progress-bar");
    this.prevNextButtons = this.querySelector(".vertical-video-carousel__prev-next");
    this._onResizeListener = throttle(this._onResize.bind(this), 15);
    this._onScrollListener = throttle(this._updateProgressBar.bind(this), 15);
    window.addEventListener("resize", this._onResizeListener);
    this.addEventListener("scrollable-content:progress", this._onScrollListener);
    this.addEventListener("prev-next:prev", this.previous.bind(this));
    this.addEventListener("prev-next:next", this.next.bind(this));
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this._onBlockSelect(event));
    }
    this._setupVideos();
    this._updateMetrics();
    this._updateProgressBar();
    this._updateNavigation();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", this._onResizeListener);
    this.removeEventListener("scrollable-content:progress", this._onScrollListener);
  }
  _setupVideos() {
    this.querySelectorAll(".vertical-video-carousel__media-wrapper").forEach((mediaWrapper) => {
      const nativeVideo = mediaWrapper.querySelector("native-video"), toggleButton = mediaWrapper.querySelector(".vertical-video-carousel__media-toggle"), item = mediaWrapper.closest(".vertical-video-carousel__item");
      if (!nativeVideo || !toggleButton || !item) {
        return;
      }
      const configureVideo = () => {
        const video = nativeVideo.querySelector("video");
        if (!video || video.hasAttribute("data-vertical-video-carousel-bound")) {
          return video;
        }
        video.controls = false;
        video.loop = false;
        video.addEventListener("play", updatePlayingState);
        video.addEventListener("pause", updatePlayingState);
        video.addEventListener("ended", updatePlayingState);
        video.setAttribute("data-vertical-video-carousel-bound", "");
        return video;
      };
      const updatePlayingState = () => {
        const video = configureVideo();
        const isPlaying = !!video && !video.paused && !video.ended;
        item.classList.toggle("is-playing", isPlaying);
      };
      const play = () => {
        const currentVideo = configureVideo();
        if (currentVideo?.ended) {
          currentVideo.currentTime = 0;
        }
        nativeVideo.play();
        requestAnimationFrame(() => {
          configureVideo();
          updatePlayingState();
        });
      };
      const pause = () => {
        nativeVideo.pause();
        requestAnimationFrame(updatePlayingState);
      };
      toggleButton.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const video = configureVideo();
        if (video && !video.paused && !video.ended) {
          pause();
        } else {
          play();
        }
      });
      nativeVideo.addEventListener("video:played", () => {
        configureVideo();
        updatePlayingState();
      });
      nativeVideo.addEventListener("video:paused", updatePlayingState);
      updatePlayingState();
    });
  }
  _updateMetrics() {
    const styles = getComputedStyle(this);
    const gap = parseFloat(styles.getPropertyValue("--vertical-video-carousel-gap")) || 0;
    const cardWidth = this.items[0] ? this.items[0].clientWidth : 0;
    const viewportWidth = this.scroller?.clientWidth || 0;
    this.visibleItems = cardWidth ? Math.max(1, Math.floor((viewportWidth + gap) / (cardWidth + gap))) : 1;
    this.stepCount = Math.max(this.items.length - this.visibleItems + 1, 1);
  }
  _getCurrentProgress() {
    const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
    if (maxScroll <= 0) {
      return 0;
    }
    const directionFlip = window.themeVariables.settings.direction === "ltr" ? 1 : -1;
    const scrollLeft = this.scroller.scrollLeft * directionFlip;
    return Math.max(0, Math.min(1, scrollLeft / maxScroll)) * 100;
  }
  _updateProgressBar(event) {
    this._updateMetrics();
    if (!this.progressBar) {
      return;
    }
    const progress = event?.detail?.progress ?? this._getCurrentProgress();
    this.progressBar.style.setProperty("--divider", this.stepCount);
    this.progressBar.style.setProperty("--transform", `${progress}%`);
    this._updateNavigation();
  }
  _onResize() {
    this._updateMetrics();
    this._updateProgressBar();
  }
  _onBlockSelect(event) {
    const selectedBlock = event.target.closest(".vertical-video-carousel__item");
    const selectedIndex = this.items.indexOf(selectedBlock);
    if (selectedIndex !== -1) {
      this.select(selectedIndex, !event.detail.load);
    }
  }
  _getClosestIndexToCenter() {
    if (!this.items.length) {
      return 0;
    }
    const targetPoint = this.scroller.scrollLeft + this.scroller.clientWidth / 2;
    const itemCenters = this.items.map((item) => Math.round(item.offsetLeft + item.clientWidth / 2));
    return itemCenters.indexOf(itemCenters.reduce((previous, current) => {
      return Math.abs(current - targetPoint) < Math.abs(previous - targetPoint) ? current : previous;
    }));
  }
  _updateNavigation() {
    if (!this.prevNextButtons) {
      return;
    }
    const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
    const currentScroll = Math.max(0, Math.abs(this.scroller.scrollLeft));
    this.prevNextButtons.isPrevDisabled = currentScroll <= 1;
    this.prevNextButtons.isNextDisabled = maxScroll - currentScroll <= 1;
  }
  previous() {
    this.select(Math.max(this._getClosestIndexToCenter() - 1, 0));
  }
  next() {
    this.select(Math.min(this._getClosestIndexToCenter() + 1, this.items.length - 1));
  }
  select(index, animate = true) {
    const selectedItem = this.items[index];
    if (!selectedItem) {
      return;
    }
    const itemBounds = selectedItem.getBoundingClientRect();
    const scrollerBounds = this.scroller.getBoundingClientRect();
    this.scroller.scrollBy({
      behavior: animate ? "smooth" : "auto",
      left: Math.floor(itemBounds.left - scrollerBounds.left - scrollerBounds.width / 2 + itemBounds.width / 2)
    });
  }
};
window.customElements.define("vertical-video-carousel", VerticalVideoCarousel);
/* Vertical Video Carousel section - end */

// Review Carousel section
// Reuses the vertical video carousel behavior for the semantic <review-carousel> custom element.
if (!customElements.get("review-carousel")) {
  class ReviewCarousel extends VerticalVideoCarousel {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll(".review-carousel__item, .vertical-video-carousel__item"));
      this.scroller = this.querySelector(".review-carousel__scroller, .vertical-video-carousel__scroller");
      this.progressBar = this.querySelector(".review-carousel__progress-bar, .vertical-video-carousel__progress-bar");
      this.prevNextButtons = this.querySelector(".review-carousel__prev-next, .vertical-video-carousel__prev-next");
      this._onResizeListener = throttle(this._onResize.bind(this), 15);
      this._onScrollListener = throttle(this._updateProgressBar.bind(this), 15);
      window.addEventListener("resize", this._onResizeListener);
      this.addEventListener("scrollable-content:progress", this._onScrollListener);
      this.addEventListener("prev-next:prev", this.previous.bind(this));
      this.addEventListener("prev-next:next", this.next.bind(this));

      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this._onBlockSelect(event));
      }

      this._setupVideos();
      this._updateMetrics();
      this._updateProgressBar();
      this._updateNavigation();
    }

    _setupVideos() {
      this.querySelectorAll(".vertical-video-carousel__media-wrapper").forEach((mediaWrapper) => {
        const nativeVideo = mediaWrapper.querySelector("native-video");
        const toggleButton = mediaWrapper.querySelector(".vertical-video-carousel__media-toggle");
        const item = mediaWrapper.closest(".review-carousel__item, .vertical-video-carousel__item");

        if (!nativeVideo || !toggleButton || !item) {
          return;
        }

        const configureVideo = () => {
          const video = nativeVideo.querySelector("video");

          if (!video || video.hasAttribute("data-vertical-video-carousel-bound")) {
            return video;
          }

          video.controls = false;
          video.loop = false;
          video.addEventListener("play", updatePlayingState);
          video.addEventListener("pause", updatePlayingState);
          video.addEventListener("ended", updatePlayingState);
          video.setAttribute("data-vertical-video-carousel-bound", "");
          return video;
        };

        const updatePlayingState = () => {
          const video = configureVideo();
          const isPlaying = !!video && !video.paused && !video.ended;
          item.classList.toggle("is-playing", isPlaying);
        };

        const play = () => {
          const currentVideo = configureVideo();

          if (currentVideo?.ended) {
            currentVideo.currentTime = 0;
          }

          nativeVideo.play();

          requestAnimationFrame(() => {
            configureVideo();
            updatePlayingState();
          });
        };

        const pause = () => {
          nativeVideo.pause();
          requestAnimationFrame(updatePlayingState);
        };

        toggleButton.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          const video = configureVideo();

          if (video && !video.paused && !video.ended) {
            pause();
          } else {
            play();
          }
        });

        nativeVideo.addEventListener("video:played", () => {
          configureVideo();
          updatePlayingState();
        });

        nativeVideo.addEventListener("video:paused", updatePlayingState);
        updatePlayingState();
      });
    }

    _updateMetrics() {
      const styles = getComputedStyle(this);
      const gap = parseFloat(styles.getPropertyValue("--review-carousel-gap")) || parseFloat(styles.getPropertyValue("--vertical-video-carousel-gap")) || 0;
      const cardWidth = this.items[0] ? this.items[0].clientWidth : 0;
      const viewportWidth = this.scroller?.clientWidth || 0;

      this.visibleItems = cardWidth ? Math.max(1, Math.floor((viewportWidth + gap) / (cardWidth + gap))) : 1;
      this.stepCount = Math.max(this.items.length - this.visibleItems + 1, 1);
    }

    _getCurrentProgress() {
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;

      if (maxScroll <= 0) {
        return 0;
      }

      const directionFlip = window.themeVariables.settings.direction === "ltr" ? 1 : -1;
      const scrollLeft = this.scroller.scrollLeft * directionFlip;
      return Math.max(0, Math.min(1, scrollLeft / maxScroll)) * 100;
    }

    _onBlockSelect(event) {
      const selectedBlock = event.target.closest(".review-carousel__item, .vertical-video-carousel__item");
      const selectedIndex = this.items.indexOf(selectedBlock);

      if (selectedIndex !== -1) {
        this.select(selectedIndex, !event.detail.load);
      }
    }
  }

  window.customElements.define("review-carousel", ReviewCarousel);
}